/**
 * LE PARCOURS D'UNE ACTION DU PLAN — module PUR (RQ, 07/10/2026).
 *
 * « La différence entre démarrer l'action et la marquer réalisée est un peu
 * compliquée. Ce serait bien de voir un état d'avancement comme dans le
 * traitement des NC. » La fiche action montrait une pastille d'état isolée et,
 * au pied du dossier, UN bouton dont le libellé ne disait ni où l'on en était,
 * ni ce que le geste engageait. Les deux gestes se ressemblent alors qu'ils
 * n'affirment pas du tout la même chose :
 *
 *   · DÉMARRER = le travail commence. Rien n'est encore acquis.
 *   · MARQUER RÉALISÉE = il n'y a plus rien à faire. C'est une affirmation,
 *     qui ouvre la vérification d'efficacité du RQ — et qui, tant qu'elle n'est
 *     pas prononcée, empêche la clôture de la NC d'origine.
 *
 * Ce module dit OÙ l'on en est, ce qu'on attend à cette étape, ce qui manque,
 * et quel est le geste suivant. Aucune règle métier ici : l'ordre des états et
 * les droits restent au moteur (`ALLOWED` dans `setActionStatus`), qui refuse
 * toute transition que cette frise laisserait croire possible.
 *
 * Même structure que `parcours-nc.ts` — volontairement, pour que les deux
 * circuits se lisent de la même façon. Mais les étapes d'une action ne sont PAS
 * ses onglets : trois d'entre elles se jouent dans le même onglet « Suivi »,
 * et les afficher en onglets aurait donné trois onglets au contenu identique.
 * La frise est donc un état d'avancement, et chaque étape mène à l'onglet où
 * elle se joue.
 */

export type EtapeAction = 'engagement' | 'execution' | 'realisation' | 'efficacite';

/** Les onglets du dossier action — où chaque étape se joue. */
export type OngletEtapeAction = 'suivi' | 'efficacite';

export interface DefEtapeAction {
  cle: EtapeAction;
  titre: string;
  /** La question à laquelle l'étape répond. */
  question: string;
  /** Ce qu'on fait ici, en une phrase. */
  consigne: string;
  /** Les états du moteur qui correspondent à cette étape. */
  statuts: readonly string[];
  /** L'onglet du dossier où cette étape se joue. */
  onglet: OngletEtapeAction;
}

export const ETAPES_ACTION: readonly DefEtapeAction[] = [
  {
    cle: 'engagement', titre: 'Inscrite', question: 'Que faut-il faire, qui le fait, pour quand ?',
    consigne: 'L’action est au plan, le travail n’a pas commencé. Écrivez ce qu’il faut faire, désignez qui en répond '
      + 'et fixez l’échéance — puis démarrez-la le jour où le travail commence réellement.',
    statuts: ['ouverte'],
    onglet: 'suivi',
  },
  {
    cle: 'execution', titre: 'En cours', question: 'Où en est le travail ?',
    consigne: 'Le travail est engagé. Complétez le suivi au fil de l’eau : c’est ce texte qui sera lu en audit. '
      + 'Ne la marquez réalisée que lorsqu’il ne reste plus rien à faire.',
    statuts: ['encours'],
    onglet: 'suivi',
  },
  {
    cle: 'realisation', titre: 'Réalisée', question: 'Le travail est-il terminé ?',
    consigne: 'Tout ce qui était prévu est fait. Reste à constater que cela a bien supprimé la cause : '
      + 'c’est l’étape suivante, et elle revient au RQ.',
    statuts: ['realisee'],
    onglet: 'suivi',
  },
  {
    cle: 'efficacite', titre: 'Efficacité vérifiée', question: 'La cause a-t-elle disparu ?',
    consigne: 'Le RQ écrit ce qu’il a constaté — par exemple aucune récidive sur 30 jours — et clôture l’action. '
      + 'Sans ce constat écrit, l’action ne peut pas être clôturée (ISO 15189 §8.7).',
    statuts: ['verifiee'],
    onglet: 'efficacite',
  },
];

export const NB_ETAPES_ACTION = ETAPES_ACTION.length;

const indexDe = (cle: EtapeAction) => ETAPES_ACTION.findIndex((e) => e.cle === cle);

/**
 * Une action ANNULÉE n'est pas une étape du parcours : elle en sort.
 *
 * Même traitement que les états latéraux d'un document (refusé, abandonné) :
 * le circuit est interrompu, on ne le dessine pas comme un chemin parcouru.
 */
export function parcoursInterrompu(statut: string): boolean {
  return statut === 'annulee';
}

/** L'étape où en est l'action. Un état inconnu (ou annulé) retombe sur l'inscription. */
export function etapeCouranteAction(statut: string): EtapeAction {
  return ETAPES_ACTION.find((e) => e.statuts.includes(statut))?.cle ?? 'engagement';
}

export function numeroAction(cle: EtapeAction): number {
  return indexDe(cle) + 1;
}

export function etapeAction(cle: EtapeAction): DefEtapeAction {
  return ETAPES_ACTION[indexDe(cle)];
}

export function suivanteAction(cle: EtapeAction): EtapeAction | null {
  return ETAPES_ACTION[indexDe(cle) + 1]?.cle ?? null;
}

export type EtatEtape = 'faite' | 'en_cours' | 'a_venir';

/** Où en est CETTE étape par rapport à l'état de l'action. */
export function etatEtapeAction(cle: EtapeAction, statut: string): EtatEtape {
  // Annulée : rien n'a été mené à son terme. On ne pare pas d'étapes franchies
  // une action qui n'a jamais été engagée — la frise le dit en toutes lettres.
  if (parcoursInterrompu(statut)) return 'a_venir';
  // L'efficacité vérifiée est le terme du parcours : la dernière étape comprise.
  if (statut === 'verifiee') return 'faite';
  const courante = indexDe(etapeCouranteAction(statut));
  const i = indexDe(cle);
  if (i < courante) return 'faite';
  return i === courante ? 'en_cours' : 'a_venir';
}

/**
 * LE GESTE SUIVANT — ce que le bouton fait, et ce qu'il AFFIRME.
 *
 * `explique` est la phrase qui manquait : le libellé seul (« Marquer réalisée »)
 * ne dit pas qu'on affirme qu'il ne reste rien à faire.
 */
export interface GesteAction {
  /** L'état visé — celui que le moteur attend. */
  statut: string;
  libelle: string;
  /** Ce que le geste affirme, en une phrase, à côté du bouton. */
  explique: string;
  /** Vrai quand le geste est réservé au RQ. */
  rqSeul: boolean;
}

export function prochainGeste(statut: string): GesteAction | null {
  if (statut === 'ouverte') {
    return {
      statut: 'encours', libelle: 'Démarrer l’action', rqSeul: false,
      explique: 'Le travail commence : l’action passe « en cours ». Rien n’est encore acquis.',
    };
  }
  if (statut === 'encours') {
    return {
      statut: 'realisee', libelle: 'Marquer réalisée', rqSeul: false,
      explique: 'Vous affirmez qu’il ne reste plus rien à faire. La vérification d’efficacité du RQ s’ouvre alors.',
    };
  }
  if (statut === 'realisee') {
    return {
      statut: 'verifiee', libelle: 'Vérifier l’efficacité et clôturer', rqSeul: true,
      explique: 'Le RQ écrit ce qu’il a constaté, et l’action est clôturée — l’état ne redescend plus.',
    };
  }
  return null;
}

/** Ce qui manque pour dire « cette étape est bien remplie » — vide si rien. Indicatif : ne bloque rien. */
export function resteAFaireAction(cle: EtapeAction, a: {
  description?: string | null; responsable?: string | null; echeance?: string | null;
  dateRealisation?: string | null; verifEfficacite?: string | null;
}): string[] {
  const manque: string[] = [];
  const vide = (v?: string | null) => !String(v || '').trim();
  if (cle === 'engagement') {
    if (vide(a.description)) manque.push('ce qu’il faut faire n’est pas écrit');
    if (vide(a.responsable)) manque.push('personne n’en répond');
    if (vide(a.echeance)) manque.push('aucune échéance — l’action ne sera jamais signalée en retard');
  }
  if (cle === 'execution' && vide(a.description)) manque.push('le suivi ne dit pas ce qui est fait');
  if (cle === 'realisation' && vide(a.dateRealisation)) manque.push('aucune date de réalisation');
  if (cle === 'efficacite' && vide(a.verifEfficacite)) manque.push('le constat d’efficacité n’est pas écrit');
  return manque;
}
